import React, { useState } from "react";
import "./RegistrationForm.css";
import RegistrationSuccess from "./RegistrationSuccess";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage, db } from "./firebase.config";
import { doc, runTransaction, setDoc } from "firebase/firestore";


const RegistrationForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [message] = useState("");
  const [errorMessage] = useState("");
  // Add the new state declarations here
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isSubmissionAccepted, setIsSubmissionAccepted] = useState(false);
    
  // Add the verification check
  const isBothVerified = isTermsAccepted && isSubmissionAccepted;
  
  const [formData, setFormData] = useState({
    senderName: "",
    senderEmail: "",
    senderPostCode: "",
    senderAddress: "",
    senderPhone: "",
    senderPassportNumber: "",
    receiverName: "",
    receiverPostCode: "",
    receiverAddress: "",
    receiverPhone: "",
    amountOfBoxes: "",
    itemDescriptions: "",
    pickupDate: "",
    pickupTime: "",
    shipmentType: "",
    passportPhoto: null,
    movingDocuments: null,
    otherDocuments: null,
  });

  const getTomorrowDate = () => {
    const today = new Date();
    const twoDaysFromNow = new Date(today);
    twoDaysFromNow.setDate(today.getDate() + 2);
    return twoDaysFromNow.toISOString().split("T")[0];
  };

  const uploadFileToFirebase = async (
    file,
    type,
    customerNumber,
    shipmentType
  ) => {
    if (!file) return null;

    const storageRef = ref(
      storage,
      `${shipmentType.toLowerCase()}/${customerNumber}/${type}_${file.name}`
    );

    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData({ ...formData, [name]: files[0] });
  };

  const handlePopupClose = () => {
    setShowSuccessPopup(false);
    setRegistrationComplete(true);
  };

  const handleShipmentTypeSelect = (type) => {
    setFormData({ ...formData, shipmentType: type });
  };

  const generateCustomerNumber = async (shipmentType) => {
    return await runTransaction(db, async (transaction) => {
      const counterDocRef = doc(db, "counters", shipmentType.toLowerCase());
      const counterDoc = await transaction.get(counterDocRef);
      const prefix = shipmentType === "Ocean" ? "HCX" : "ECX";
      let lastNumber = counterDoc.exists() ? counterDoc.data().lastNumber : 0;
      let newNumber = Number(lastNumber) + 1;
      let proposedCustomerNumber = `${prefix}${String(newNumber).padStart(
        6,
        "0"
      )}`;

      const collectionName =
        shipmentType === "Ocean" ? "ocean_customers" : "air_customers";
      const customerDocRef = doc(db, collectionName, proposedCustomerNumber);
      const customerDoc = await transaction.get(customerDocRef);

      while (customerDoc.exists()) {
        newNumber++;
        proposedCustomerNumber = `${prefix}${String(newNumber).padStart(
          6,
          "0"
        )}`;
        const nextCustomerDocRef = doc(
          db,
          collectionName,
          proposedCustomerNumber
        );
        const nextCustomerDoc = await transaction.get(nextCustomerDocRef);
        if (!nextCustomerDoc.exists()) break;
      }

      transaction.update(counterDocRef, { lastNumber: newNumber });
      return proposedCustomerNumber;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.shipmentType) {
      toast.error("Please select a shipment type (LAUT or UDARA)");
      return;
    }
    
    setIsLoading(true);
    setShowLoadingOverlay(true);
    setUploadingFiles(true);

    try {
      const customerNumber = await generateCustomerNumber(
        formData.shipmentType
      );

      const passportPhotoURL = await uploadFileToFirebase(
        formData.passportPhoto,
        "passportPhoto",
        customerNumber,
        formData.shipmentType
      );

      const movingDocumentsURL = await uploadFileToFirebase(
        formData.movingDocuments,
        "movingDocuments",
        customerNumber,
        formData.shipmentType
      );

      const otherDocumentsURL = await uploadFileToFirebase(
        formData.otherDocuments,
        "otherDocuments",
        customerNumber,
        formData.shipmentType
      );

      // Create a clean data object without File objects
      const customerData = {
        senderName: formData.senderName,
        senderEmail: formData.senderEmail,
        senderPostCode: formData.senderPostCode,
        senderAddress: formData.senderAddress,
        senderPhone: formData.senderPhone,
        senderPassportNumber: formData.senderPassportNumber,
        receiverName: formData.receiverName,
        receiverPostCode: formData.receiverPostCode,
        receiverAddress: formData.receiverAddress,
        receiverPhone: formData.receiverPhone,
        amountOfBoxes: formData.amountOfBoxes,
        itemDescriptions: formData.itemDescriptions,
        pickupDate: formData.pickupDate,
        pickupTime: formData.pickupTime,
        shipmentType: formData.shipmentType,
        customerNumber,
        passportPhotoURL,
        movingDocumentsURL,
        otherDocumentsURL,
        registrationDate: new Date().toISOString(),
      };

      const collectionName =
        formData.shipmentType === "Ocean" ? "ocean_customers" : "air_customers";
      await setDoc(doc(db, collectionName, customerNumber), customerData);

      setFormData((prevData) => ({
        ...prevData,
        customerNumber,
        passportPhotoURL,
        movingDocumentsURL,
        otherDocumentsURL,
      }));

      setShowLoadingOverlay(false);
      setShowSuccessPopup(true);
    } catch (error) {
      toast.error("Registration failed. Please try again.");
      console.error("Error details:", error);
    } finally {
      setIsLoading(false);
      setUploadingFiles(false);
      setShowLoadingOverlay(false);
    }
  };
  
  return (
    <>
      <ToastContainer />
      {!registrationComplete ? (
        <div className="registration-form">
          <div className="logo-container">
            <img 
              src="/assets/logo.png" 
              alt="Hebat Cargo Logo" 
              className="company-logo"
            />
          </div>
          <h2>Formulir Pendaftaran</h2>
          
          {/* Shipment Type Selection Cards */}
          <div className="form-section">
            <h3>Pilih Layanan Kiriman</h3>
            <div className="shipment-type-cards">
              <div 
                className={`shipment-card ${formData.shipmentType === 'Ocean' ? 'selected' : ''}`}
                onClick={() => handleShipmentTypeSelect('Ocean')}
              >
                <h3>LAUT</h3>
                <p>Pengiriman via laut</p>
              </div>
              <div 
                className={`shipment-card ${formData.shipmentType === 'Air' ? 'selected' : ''}`}
                onClick={() => handleShipmentTypeSelect('Air')}
              >
                <h3>UDARA</h3>
                <p>Pengiriman via udara</p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>Informasi Pengirim</h3>
              <label>Nama Pengirim:</label>
              <input
                type="text"
                name="senderName"
                value={formData.senderName}
                onChange={handleChange}
                required
              />

              <label>Email Pengirim:</label>
              <input
                type="email"
                name="senderEmail"
                value={formData.senderEmail}
                onChange={handleChange}
                required
              />

              <label>Kode Pos Pengirim:</label>
              <input
                type="text"
                name="senderPostCode"
                value={formData.senderPostCode}
                onChange={handleChange}
                required
              />

              <label>Alamat Lengkap Pengirim:</label>
              <textarea
                name="senderAddress"
                value={formData.senderAddress}
                onChange={handleChange}
                required
              />

              <label>Nomer Telpon Pengirim:</label>
              <input
                type="tel"
                name="senderPhone"
                value={formData.senderPhone}
                onChange={handleChange}
                required
              />

              <label>Nomer Passport:</label>
              <input
                type="text"
                name="senderPassportNumber"
                value={formData.senderPassportNumber}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-section">
              <h3>Informasi Penerima</h3>
              <label>Nama Penerima:</label>
              <input
                type="text"
                name="receiverName"
                value={formData.receiverName}
                onChange={handleChange}
                required
              />

              <label>Kode Pos Penerima:</label>
              <input
                type="text"
                name="receiverPostCode"
                value={formData.receiverPostCode}
                onChange={handleChange}
                required
              />

              <label>Alamat Penerima:</label>
              <textarea
                name="receiverAddress"
                value={formData.receiverAddress}
                onChange={handleChange}
                required
              />

              <label>Nomer Telpon Penerima:</label>
              <input
                type="tel"
                name="receiverPhone"
                value={formData.receiverPhone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-section">
              <h3>Detail Kiriman</h3>
              <label>Jumlah Box/Koper/Paket:</label>
              <input
                type="number"
                name="amountOfBoxes"
                value={formData.amountOfBoxes}
                onChange={handleChange}
                required
              />

              <label>Diskripsi Barang-Barang yang akan dikirim:</label>
              <textarea
                name="itemDescriptions"
                value={formData.itemDescriptions}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-section">
              <h3>Jadwal Pickup</h3>
              <label>Tanggal Pickup:</label>
              <input
                type="date"
                name="pickupDate"
                value={formData.pickupDate}
                onChange={handleChange}
                min={getTomorrowDate()}
                required
              />

              <label>Jam Pickup:</label>
              <select
                name="pickupTime"
                value={formData.pickupTime}
                onChange={handleChange}
                required
              >
                <option value="">Pilih Jam Pickup</option>
                <option value="09:00-12:00">08:00-12:00</option>
                <option value="12:00-14:00">12:00-14:00</option>
                <option value="14:00-16:00">14:00-16:00</option>
                <option value="16:00-18:00">16:00-18:00</option>
                <option value="18:00-21:00">18:00-21:00</option>
              </select>
            </div>

            <div className="form-section">
              <h3>File Upload</h3>
              {uploadingFiles && (
                <div className="upload-indicator">
                  <span className="spinner"></span> Uploading files...
                </div>
              )}
              <label>Foto Passport:</label>
              <input
                type="file"
                name="passportPhoto"
                onChange={handleFileChange}
              />

              <label>Dokumen Pindahan:</label>
              <input
                type="file"
                name="movingDocuments"
                onChange={handleFileChange}
              />

              <label>Dokumen Lainnya:</label>
              <input
                type="file"
                name="otherDocuments"
                onChange={handleFileChange}
              />
            </div>
            <div className="form-section">
              <div className="terms-container">
                <input 
                  type="checkbox" 
                  id="termsCheckbox" 
                  checked={isTermsAccepted}
                  onChange={(e) => setIsTermsAccepted(e.target.checked)}
                  required 
                />
                <label htmlFor="termsCheckbox">
                  Saya telah membaca dan menyetujui 
                  <a 
                    href="/assets/terms-and-conditions.html" 
                    target="_blank" 
                    className="terms-link"
                    rel="noopener noreferrer"
                  > 
                     Syarat dan Ketentuan yang berlaku
                  </a>
                </label>
              </div>

              <div className="verification-container">
                <label className="verification-label">
                  <input
                    type="checkbox"
                    checked={isSubmissionAccepted}
                    onChange={(e) => setIsSubmissionAccepted(e.target.checked)}
                    required
                  />
                  Saya setuju untuk mengirimkan pendaftaran ini.
                </label>
              </div>
            </div>

            <button type="submit" disabled={!isBothVerified || isLoading}>
              {isLoading ? "Submitting..." : "Daftar"}
            </button>
          </form>
          {showLoadingOverlay && (
            <div className="loading-overlay">
              <div className="loader"></div>
              <p>Processing Registration...</p>
            </div>
          )}

          {showSuccessPopup && (
            <div className="popup-overlay">
              <div className="popup">
                <h2>Registrasi Berhasil!</h2>
                <p>Email konfirmasi telah dikirim ke alamat email Anda.</p>
                <button onClick={handlePopupClose}>
                  Lihat Detail Registrasi
                </button>
              </div>
            </div>
          )}

          {message && <p className="message">{message}</p>}
          {errorMessage && <p className="error-message">{errorMessage}</p>}
        </div>
      ) : (
        <RegistrationSuccess customerData={formData} />
      )}
    </>
  );
};

export default RegistrationForm;
